import {
  normalizeNodeUid,
  percentRecommendedTimeFromDomTimeFields,
  showFinishCardContentAndSection,
} from './case_experience_in_rails';
import {
  hideLowTimeCaseModal,
  showLowTimeCaseModal,
  persistLowTimeModalPending,
} from './low_time_case_modal';
import {
  hideCaseCompletionModal,
  showCaseCompletionModal,
  persistCaseCompletionModalPending,
} from './case_completion_modal';

let caseCompletionModalAutocloseTimeoutId = null;
let confettiIntervalId = null;
let confettiLoadPromise = null;

const CONFETTI_Z_INDEX = 20000;

export function cancelCaseCompletionModalAutoclose() {
  if (caseCompletionModalAutocloseTimeoutId != null) {
    clearTimeout(caseCompletionModalAutocloseTimeoutId);
    caseCompletionModalAutocloseTimeoutId = null;
  }
}

export function finishCaseCardJustRevealed(response) {
  if (response?.state?.finish_case_card?.invisible?.value === false) return true;

  const finishSectionUidRaw = document
    .getElementById('document-container')
    ?.getAttribute('data-case-experience--case-viewer-finish-section-uid-value');
  if (!finishSectionUidRaw || !response?.state || typeof response.state !== 'object') return false;

  const normalizedFinishSectionUid = normalizeNodeUid(finishSectionUidRaw);
  const state = response.state;

  if (state[normalizedFinishSectionUid]?.invisible?.value === false) return true;

  const spacedFinishSectionUid = normalizedFinishSectionUid.split('-').join(' ');
  if (state[spacedFinishSectionUid]?.invisible?.value === false) return true;

  return false;
}

export function hideStudentRailsFeedbackCardModal() {
  document.querySelectorAll('.doc-section.feedback_card .gen-modal').forEach((el) => {
    el.style.display = 'none';
  });
}

/** Unlock/apply_state percent first; else live time + recommended (not stale percent hidden input). */
function percentForCompletionModals(apiPayload) {
  const rawApi = apiPayload?.calculate_percentage_of_recommended_time;
  if (rawApi !== undefined && rawApi !== null) {
    const fromApi = Number(rawApi);
    if (Number.isFinite(fromApi)) return fromApi;
  }
  const fromTimeFields = percentRecommendedTimeFromDomTimeFields();
  if (fromTimeFields != null) return fromTimeFields;
  return 100;
}

/**
 * Run completion UX after unlock. Turbo streams are already rendered in continueToNextSection
 * before this is called, so turbo:after-stream-render will not fire again.
 */
export function scheduleStudentCaseCompletionModalsAfterUnlock(apiPayload = null) {
  const run = () => runStudentCaseCompletionModals({ apiPayload });

  if (typeof document === 'undefined') {
    run();
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

/**
 * canvas-confetti defaults to a blob Worker; app CSP allows script-src 'self' https: only.
 * useWorker: false keeps confetti on the main thread (same visual, CSP-safe).
 */
async function loadCspSafeConfettiFire() {
  if (window.__caseExperienceConfettiFire) return window.__caseExperienceConfettiFire;
  if (!confettiLoadPromise) {
    confettiLoadPromise = import('canvas-confetti')
      .then((module) => {
        const fire = module.default.create(null, { useWorker: false, resize: true });
        window.__caseExperienceConfettiFire = fire;
        return fire;
      })
      .catch((err) => {
        confettiLoadPromise = null;
        throw err;
      });
  }
  return confettiLoadPromise;
}

/** Module-scoped confetti so turbo disconnect on button-action does not cancel it. */
export async function startStudentCaseConfetti() {
  if (confettiIntervalId != null) return;

  try {
    const fire = await loadCspSafeConfettiFire();
    let elapsed = 0;
    confettiIntervalId = window.setInterval(() => {
      [0, 0.5, 1].forEach((x) => {
        fire({
          particleCount: 200,
          spread: 120,
          gravity: 2.5,
          scalar: 1.5,
          startVelocity: 60,
          origin: { x, y: 0 },
          zIndex: CONFETTI_Z_INDEX,
        });
      });
      elapsed += 400;
      if (elapsed >= 2000) {
        stopStudentCaseConfetti();
      }
    }, 400);
  } catch (err) {
    console.error('Case completion confetti failed to load:', err);
  }
}

export function stopStudentCaseConfetti() {
  if (confettiIntervalId == null) return;
  clearInterval(confettiIntervalId);
  confettiIntervalId = null;
}

async function restoreFinishCardAfterModalOpenFailure() {
  await showFinishCardContentAndSection();
}

/**
 * Low-time vs completion modal UX after finish case card is revealed (student 2-col).
 */
export async function runStudentCaseCompletionModals({
  startConfetti = () => {
    void startStudentCaseConfetti();
  },
  stopConfetti = stopStudentCaseConfetti,
  apiPayload = null,
} = {}) {
  const documentContainer = document.getElementById('document-container');
  const isStudent =
    documentContainer?.getAttribute('data-case-experience--case-viewer-is-student-value') === 'true';
  const percentOfRecommendedTime = percentForCompletionModals(apiPayload);
  if (!isStudent) return;

  cancelCaseCompletionModalAutoclose();
  hideStudentRailsFeedbackCardModal();

  hideCaseCompletionModal();
  hideLowTimeCaseModal();

  if (percentOfRecommendedTime < 25) {
    persistCaseCompletionModalPending(false);
    persistLowTimeModalPending(true);
    const opened = await showLowTimeCaseModal();
    if (!opened) {
      persistLowTimeModalPending(false);
      await restoreFinishCardAfterModalOpenFailure();
    }
    return;
  }

  // Normal completion path — drop any stale low-time pending from a prior attempt.
  persistLowTimeModalPending(false);
  persistCaseCompletionModalPending(true);
  startConfetti();
  const opened = await showCaseCompletionModal();
  if (!opened) {
    persistCaseCompletionModalPending(false);
    stopConfetti();
    await restoreFinishCardAfterModalOpenFailure();
    return;
  }

  armCaseCompletionModalAutocloseIfNeeded({ stopConfetti });
}

/** Celebration (no student-choice) modals auto-dismiss after 5s; re-arm on restore too. */
export function armCaseCompletionModalAutocloseIfNeeded({
  stopConfetti = stopStudentCaseConfetti,
} = {}) {
  const hasStudentChoiceCasesInput = document.querySelector('input[name="hasStudentChoiceCases"]');
  const hasStudentChoiceCasesValue = hasStudentChoiceCasesInput?.value === 'true';
  if (hasStudentChoiceCasesValue) return false;

  cancelCaseCompletionModalAutoclose();
  caseCompletionModalAutocloseTimeoutId = window.setTimeout(() => {
    caseCompletionModalAutocloseTimeoutId = null;
    persistCaseCompletionModalPending(false);
    stopConfetti();
    void showFinishCardContentAndSection().finally(() => {});
  }, 5000);
  return true;
}
